import { type CSSProperties, useState } from "react";
import { Link } from "react-router-dom";

const scenarios = [
  {
    id: "questions",
    label: "Customer questions",
    prompt: "The same question arrives on the website, Instagram, and Messenger.",
    current: ["Find the right answer", "Check stock or policy", "Remember who asked", "Reply in the right channel"],
    system: "One connected conversation layer",
    outcome: "Approved answers, customer intent, and the next action stay attached to the conversation.",
    examine: ["Where questions arrive", "What knowledge is approved", "When a person should step in"],
    visual: "/visuals/marky-workflow-world.webp",
    visualAlt: "Marky connecting tangled customer questions into one clear conversation system",
  },
  {
    id: "handoffs",
    label: "Internal handoffs",
    prompt: "A task moves between people, but the reason, status, and next step do not move with it.",
    current: ["Ask for an update", "Search the thread", "Rebuild the context", "Chase the next owner"],
    system: "A handoff that carries its own context",
    outcome: "The task arrives with its history, decision points, owner, and a visible recovery path.",
    examine: ["Where context gets lost", "Who really owns the next move", "What should happen when work stalls"],
    visual: "/visuals/marky-friction-handoff.webp",
    visualAlt: "Marky carrying customer context across a translucent bridge to the next owner",
  },
  {
    id: "admin",
    label: "Repeated admin",
    prompt: "A small routine keeps returning, and somebody still has to remember every step.",
    current: ["Open several tools", "Copy the details", "Check the rules", "Send the follow-up"],
    system: "A quiet workflow with review built in",
    outcome: "Routine steps move automatically while exceptions remain visible to the right person.",
    examine: ["What repeats predictably", "Where judgement is still needed", "How a failed run should recover"],
    visual: "/visuals/marky-friction-admin.webp",
    visualAlt: "Marky operating a circular admin workflow that resolves repeated tasks into one completed stack",
  },
] as const;

export function FrictionFinder() {
  const [activeId, setActiveId] = useState<(typeof scenarios)[number]["id"]>("questions");
  const active = scenarios.find((scenario) => scenario.id === activeId) ?? scenarios[0];

  return (
    <section className="friction-finder page-section" aria-labelledby="friction-title">
      <div className="friction-heading" data-reveal>
        <h2 id="friction-title">Where does the day leak attention?</h2>
        <p>Choose the kind of friction you recognize. This is the first lens we use before recommending any software or AI.</p>
      </div>

      <div className="friction-tabs" role="tablist" aria-label="Choose a source of business friction" data-reveal>
        {scenarios.map((scenario, index) => (
          <button
            key={scenario.id}
            type="button"
            role="tab"
            aria-selected={active.id === scenario.id}
            aria-controls="friction-panel"
            className={active.id === scenario.id ? "is-active" : ""}
            onClick={() => setActiveId(scenario.id)}
          >
            <span>0{index + 1}</span>{scenario.label}
          </button>
        ))}
      </div>

      <div id="friction-panel" className="friction-stage" role="tabpanel" key={active.id} aria-live="polite">
        <div
          className={`friction-map friction-map--${active.id}`}
          style={{ "--friction-image": `url(${active.visual})` } as CSSProperties}
        >
          <img src={active.visual} alt={active.visualAlt} />
        </div>

        <div className="friction-reading">
          <p className="friction-prompt">{active.prompt}</p>
          <h3>{active.system}</h3>
          <p>{active.outcome}</p>
          <div>
            <small>What we would examine first</small>
            <ul>{active.examine.map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div className="friction-now">
            <small>What currently happens</small>
            <p>{active.current.join(". ")}.</p>
          </div>
          <Link className="text-action text-action--light" to="/contact">Bring us your version of this <span aria-hidden="true">↗</span></Link>
        </div>
      </div>
    </section>
  );
}
